import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "je_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1) => {
    setItems((prev) => {
      const hasColors = product.color_breakdown && Object.keys(product.color_breakdown).length > 0;
      const techQty = Number(product.technician_qty) || 0;
      const techPrice = Number(product.technician_price) || 0;
      const hasSelOpts = product.selected_options && Object.keys(product.selected_options).length > 0;
      // Cable with colors OR product with technician add-on OR product with selected options: always a new line
      if (hasColors || techQty > 0 || hasSelOpts) {
        const unit = product.is_promo && product.discount_percent
          ? Math.round(product.price * (100 - product.discount_percent)) / 100
          : product.price;
        return [...prev, {
          product_id: product.product_id,
          name: product.name,
          price: unit,
          quantity: qty,
          image: product.images?.[0] || null,
          stock: product.stock,
          color_breakdown: hasColors ? product.color_breakdown : null,
          technician_qty: techQty,
          technician_price: techPrice,
          unit_of_sale: product.unit_of_sale || "unit",
          selected_options: hasSelOpts ? product.selected_options : null,
          line_id: `${product.product_id}-${Date.now()}`,
        }];
      }
      const idx = prev.findIndex((p) => p.product_id === product.product_id && !p.color_breakdown && !(p.technician_qty > 0) && !p.selected_options);
      const unit = product.is_promo && product.discount_percent
        ? Math.round(product.price * (100 - product.discount_percent)) / 100
        : product.price;
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [...prev, {
        product_id: product.product_id,
        name: product.name,
        price: unit,
        quantity: qty,
        image: product.images?.[0] || null,
        stock: product.stock,
        unit_of_sale: product.unit_of_sale || "unit",
      }];
    });
  };

  const updateQty = (identifier, qty) => {
    setItems((prev) => prev.map((p) => {
      const match = p.line_id ? p.line_id === identifier : p.product_id === identifier;
      return match ? { ...p, quantity: Math.max(1, qty) } : p;
    }));
  };

  const removeItem = (identifier) => {
    setItems((prev) => prev.filter((p) => {
      const match = p.line_id ? p.line_id === identifier : p.product_id === identifier;
      return !match;
    }));
  };

  const clear = () => setItems([]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, p) => {
      const tech = (Number(p.technician_qty) || 0) * (Number(p.technician_price) || 0);
      return s + p.price * p.quantity + tech;
    }, 0);
    const count = items.reduce((s, p) => s + p.quantity, 0);
    return { subtotal, count };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, totals }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
